"use client"
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { invalidatePlaybookScrollMeasurementCaches } from '../data/playbookScrollUtils'

// Animated expansion for the three end-of-Plan-A endings (Epilogue / Public
// POV / Insider POV). The bodies stay mounted at height 0 (see the prerender
// flag in PlaybookBranchNodeMarkdownContent), so opening is a pure height
// animation — no mount work on click.

// One key per ending, shared by the sequencing hook and its callers.
export type EndingKey = 'epilogue' | 'public' | 'insider'

// How long the height transition runs. Must match the Tailwind duration on
// the wrapper div below; useSequencedEnding waits this long before opening
// the next ending so the close animation is visible.
const ENDING_ANIMATION_MS = 300

// Sequences ending swaps: opening from fully closed (and closing) apply
// immediately, but switching straight from one open ending to another first
// animates the open one down to 0, then opens the target — a quick
// close-then-open so the swap reads as two motions.
export const useSequencedEnding = (target: EndingKey | null): EndingKey | null => {
  const [displayed, setDisplayed] = useState<EndingKey | null>(target)
  const displayedRef = useRef(displayed)
  displayedRef.current = displayed

  useEffect(() => {
    const current = displayedRef.current
    if (target === current) return
    if (target === null || current === null) {
      setDisplayed(target)
      return
    }
    setDisplayed(null)
    const timeout = setTimeout(() => setDisplayed(target), ENDING_ANIMATION_MS)
    return () => clearTimeout(timeout)
  }, [target])

  return displayed
}

// Height-0 ↔ auto collapse. Height is driven imperatively (0 → measured px →
// auto on transitionend) because CSS can't transition to `auto`; overflow is
// clipped only while closed/animating so open content (footnote popovers etc.)
// isn't cut off. Closed content is `inert` so it's untabbable and unclickable,
// but its ids stay reachable for the hash-scroll machinery. Closed content is
// also a [data-hud-date-ignore] scope: the clipped body still lays out at full
// height, so its :::dateMarker anchors would otherwise feed the HUD's
// scroll-date scan (getLatestVisibleChartDate) and drag the year off the
// story the reader is actually in. Only the OPEN ending's markers may drive
// the HUD clock — that's what resets it to the POV's own first year (2026/27)
// and lets it progress year by year through that ending's sections.
export const EndingCollapse = ({ open, children }: { open: boolean; children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null)
  const hasMountedRef = useRef(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.toggleAttribute('inert', !open)
    el.toggleAttribute('data-hud-date-ignore', !open)
    // The scan caches marker measurements keyed on scrollHeight×innerHeight;
    // flipping the ignore scope changes which markers count without itself
    // changing layout, so drop the caches rather than hoping the height
    // animation lands on a fresh signature.
    invalidatePlaybookScrollMeasurementCaches()
    // Initial mount: land directly in the resting state, no animation (a
    // deep-linked open ending shouldn't sweep open on load).
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      el.style.height = open ? 'auto' : '0px'
      el.style.overflow = open ? 'visible' : 'clip'
      return
    }
    el.style.overflow = 'clip'
    if (open) {
      el.style.height = '0px'
      el.getBoundingClientRect() // flush so the 0 → px change transitions
      el.style.height = `${el.scrollHeight}px`
    } else {
      el.style.height = `${el.scrollHeight}px`
      el.getBoundingClientRect()
      el.style.height = '0px'
    }
  }, [open])

  // flow-root: contain the body's own margins (e.g. its leading h1's
  // margin-top) so they can't collapse out of the box — a closed ending must
  // occupy exactly 0px, and the gap under the end-cap nav is owned solely by
  // the endings wrapper in PlaybookBranchNodeMarkdownContent.
  return (
    <div
      ref={ref}
      onTransitionEnd={(e) => {
        const el = ref.current
        if (!el || e.target !== el || e.propertyName !== 'height') return
        if (open) {
          // Rest at auto so the content can reflow freely while open.
          el.style.height = 'auto'
          el.style.overflow = 'visible'
        }
      }}
      className="flow-root transition-[height] duration-300 ease-in-out"
    >
      {children}
    </div>
  )
}
